import React from "react";
import { Link } from "react-router-dom";
import {
  CalendarCheck, Sparkles, Wand2, Check, ArrowRight, Plug, ListChecks, Repeat,
} from "lucide-react";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useI18n } from "@/context/I18nContext";

export default function ForHostsPage() {
  const { t } = useI18n();

  const steps = [
    { icon: Plug, t: t("hosts.how_1_t"), d: t("hosts.how_1_d") },
    { icon: ListChecks, t: t("hosts.how_2_t"), d: t("hosts.how_2_d") },
    { icon: Repeat, t: t("hosts.how_3_t"), d: t("hosts.how_3_d") },
  ];
  const perks = [t("hosts.get_1"), t("hosts.get_2"), t("hosts.get_3"), t("hosts.get_4"), t("hosts.get_5")];
  const ical = [t("hosts.ical_1"), t("hosts.ical_2"), t("hosts.ical_3"), t("hosts.ical_4")];

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col" data-testid="for-hosts-page">
      <SEO
        title="Airbnb auto-cleaning — Ever Clean Home"
        description="Connect your Airbnb calendar once. We automatically clean between every guest checkout. Hands-off turnover for Boston-area hosts."
      />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#E5E7EB]">
        <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#0EA5A4]/10 blur-3xl" aria-hidden />
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center relative">
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0EA5A4]/10 text-[#0F766E] text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" /> {t("hosts.hero_badge")}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mt-5 leading-[1.05]" data-testid="hosts-hero-title">
              {t("hosts.hero_title")}
            </h1>
            <p className="mt-5 text-lg text-[#4B5563] max-w-xl">
              {t("hosts.hero_sub")}
            </p>
            <div className="mt-8">
              <Link to="/host/connect">
                <Button className="rounded-xl h-12 px-7 text-base bg-[#0EA5A4] hover:bg-[#0F766E] text-white shadow-sm" data-testid="hosts-hero-cta">
                  {t("hosts.hero_cta")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Calendar → cleaning mock */}
          <div className="relative animate-in fade-in slide-in-from-bottom-4 duration-700 [animation-delay:150ms]">
            <Card className="rounded-3xl p-6 border border-[#E5E7EB] shadow-xl bg-white">
              <div className="flex items-center gap-3 pb-4 border-b border-[#E5E7EB]">
                <span className="w-10 h-10 rounded-xl bg-[#0EA5A4] grid place-items-center text-white"><CalendarCheck className="w-5 h-5" /></span>
                <div>
                  <div className="font-display font-bold">Guest checkout detected</div>
                  <div className="text-xs text-[#9CA3AF]">Beacon Hill Studio · synced from Airbnb</div>
                </div>
              </div>
              <div className="py-5 space-y-3">
                {["Sat — Guest checks out", "Sun — We clean (auto-scheduled)", "Sun — Ready for next guest"].map((row, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full grid place-items-center ${i === 1 ? "bg-[#0EA5A4] text-white" : "bg-[#F5F5F4] text-[#0EA5A4]"}`}>
                      {i === 1 ? <Wand2 className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                    </span>
                    <span className={`text-sm ${i === 1 ? "font-semibold" : "text-[#4B5563]"}`}>{row}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-xl bg-[#0EA5A4]/[0.06] border border-[#0EA5A4]/20 px-4 py-3 text-sm text-[#0F766E] font-medium">
                You did nothing. That's the point.
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20 w-full">
        <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">{t("hosts.how_title")}</h2>
        <div className="grid md:grid-cols-3 gap-5 mt-8">
          {steps.map((s, i) => (
            <Card key={i} className="rounded-2xl p-6 border border-[#E5E7EB]" data-testid={`hosts-step-${i + 1}`}>
              <div className="flex items-center gap-3">
                <span className="w-11 h-11 rounded-xl bg-[#0C1A1A] text-white grid place-items-center"><s.icon className="w-5 h-5" /></span>
                <span className="font-mono text-xs uppercase tracking-wider text-[#0EA5A4] font-bold">Step {i + 1}</span>
              </div>
              <div className="mt-4 font-display text-lg font-bold">{s.t}</div>
              <p className="mt-1.5 text-sm text-[#4B5563]">{s.d}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* What you get */}
      <section className="bg-[#0C1A1A] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">{t("hosts.get_title")}</h2>
            <p className="mt-3 text-white/60 max-w-md">Built for hosts who'd rather grow their listings than chase cleaners.</p>
            <Link to="/host/connect" className="inline-flex mt-7">
              <Button className="rounded-xl bg-[#FACC15] text-[#0C1A1A] hover:bg-[#eab308] font-semibold" data-testid="hosts-perks-cta">
                {t("hosts.final_cta")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <ul className="space-y-3">
            {perks.map((p, i) => (
              <li key={i} className="flex items-start gap-3 bg-white/5 rounded-xl px-4 py-3" data-testid={`hosts-perk-${i + 1}`}>
                <span className="w-6 h-6 rounded-full bg-[#0EA5A4] grid place-items-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5" /></span>
                <span className="text-sm text-white/90">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* iCal tutorial */}
      <section className="max-w-3xl mx-auto px-4 md:px-8 py-16 md:py-20 w-full">
        <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter">{t("hosts.ical_title")}</h2>
        <ol className="mt-8 space-y-4">
          {ical.map((step, i) => (
            <li key={i} className="flex items-start gap-4" data-testid={`hosts-ical-step-${i + 1}`}>
              <span className="w-9 h-9 rounded-xl bg-[#0EA5A4]/10 text-[#0F766E] grid place-items-center font-bold shrink-0">{i + 1}</span>
              <span className="text-[#0C1A1A] pt-1.5 break-words">{step}</span>
            </li>
          ))}
        </ol>
        <div className="mt-10 text-center">
          <Link to="/host/connect">
            <Button className="rounded-xl h-12 px-7 text-base bg-[#0EA5A4] hover:bg-[#0F766E] text-white" data-testid="hosts-final-cta">
              {t("hosts.final_cta")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <div className="mt-auto"><Footer /></div>
    </div>
  );
}
